import { useState, useEffect, useRef } from 'react';
import { api } from '../../apiClient';
import MobileFilterScreen from './MobileFilterScreen';
import MobileQuestionView from './MobileQuestionView';
import ZoomModal from '../ZoomModal';
import MobileAppHeader, { CH, I, IconBtn } from './MobileAppHeader';
import {
  parseSelectableValues,
  toSortedUniqueList,
  calculateProgressData,
} from '../../utils/questionDataUtils';

const BASE = 'https://docs.aws.amazon.com/ja_jp/aws-certification/latest';
const EXAM_GUIDE_URLS = {
  aifc01: `${BASE}/ai-practitioner-01/ai-practitioner-01.html`,
  aipc01: `${BASE}/ai-professional-01/ai-professional-01.html`,
  ansc01: `${BASE}/advanced-networking-specialty-01/advanced-networking-specialty-01.html`,
  clfc02: `${BASE}/cloud-practitioner-02/cloud-practitioner-02.html`,
  deac01: `${BASE}/data-engineer-associate-01/data-engineer-associate-01.html`,
  dopc02: `${BASE}/devops-engineer-professional-02/devops-engineer-professional-02.html`,
  dvac02: `${BASE}/developer-associate-02/developer-associate-02.html`,
  mlac01: `${BASE}/machine-learning-engineer-associate-01/machine-learning-engineer-associate-01.html`,
  mlsc01: `${BASE}/machine-learning-specialty-01/machine-learning-specialty-01.html`,
  saac03: `${BASE}/solutions-architect-associate-03/solutions-architect-associate-03.html`,
  sapc02: `${BASE}/solutions-architect-professional-02/solutions-architect-professional-02.html`,
  soac03: `${BASE}/sysops-administrator-associate-03/sysops-administrator-associate-03.html`,
};

const statusLabelMap = { correct: '正解', incorrect: '不正解', excluded: '除外' };
const statusCodeMap  = { '正解': 'correct', '不正解': 'incorrect', '除外': 'excluded' };

function MobileExamView({ selectedCertification, user, onLogout }) {
  const [questions, setQuestions]         = useState([]);
  const [allQuestions, setAllQuestions]   = useState([]);
  const [results, setResults]             = useState({});
  const [progressData, setProgressData]   = useState(null);
  const [currentIndex, setCurrentIndex]   = useState(0);
  const [loading, setLoading]             = useState(true);
  const [showFilter, setShowFilter]       = useState(true);
  const [zoomImageUrl, setZoomImageUrl]   = useState(null);
  const [showAnswer, setShowAnswer]       = useState(false);

  const [selectedTopic, setSelectedTopic]       = useState([]);
  const [categoryOptions, setCategoryOptions]   = useState([]);
  const [selectedCategory, setSelectedCategory] = useState([]);
  const [taskOptions, setTaskOptions]           = useState([]);
  const [selectedTask, setSelectedTask]         = useState([]);
  const [selectedResult, setSelectedResult]     = useState([]);
  const [questionOrder, setQuestionOrder]       = useState('first');
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [resumeData, setResumeData]             = useState(null);
  const [certificationId, setCertificationId]   = useState(null);
  const [certFormalName, setCertFormalName]     = useState(null);
  const [certVendor, setCertVendor]             = useState(null);
  const [certUserStatus, setCertUserStatus]     = useState(null);

  const saveTimerRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) { setLoading(false); return; }

      let certificationData;
      try {
        certificationData = await api.get(`/certifications/${selectedCertification}`);
      } catch { setLoading(false); return; }

      if (!certificationData?.id) { setLoading(false); return; }

      setCertificationId(certificationData.id);
      setCertFormalName(certificationData.formal_name ?? null);
      setCertVendor(certificationData.vendor ?? null);
      setCertUserStatus(certificationData.user_status ?? null);

      let questionData;
      try {
        questionData = await api.get('/questions', { certificationId: certificationData.id });
      } catch { setLoading(false); return; }

      const resultMap = {};
      const normalized = (questionData || []).map((q) => {
        if (q.user_status) resultMap[q.id] = q.user_status;
        const { user_status, ...rest } = q; // eslint-disable-line no-unused-vars
        return rest;
      });

      setAllQuestions(normalized);
      setQuestions(normalized);
      setResults(resultMap);

      const catNames = toSortedUniqueList(normalized.flatMap(q => parseSelectableValues(q?.category)));
      setCategoryOptions(catNames);

      const taskNames = toSortedUniqueList(normalized.flatMap(q => parseSelectableValues(q?.task)));
      setTaskOptions(taskNames);

      setProgressData(calculateProgressData(normalized, resultMap, catNames, taskNames));

      // localStorage 高速パス
      const lsId = localStorage.getItem(`lastQuestion_${selectedCertification}`);
      const lsFilter = localStorage.getItem(`lastFilter_${selectedCertification}`);
      if (lsId && normalized.some(q => String(q.id) === lsId)) {
        setResumeData({ questionId: lsId, filterState: lsFilter ? JSON.parse(lsFilter) : {} });
      }

      // API クロスデバイス
      try {
        const apiResume = await api.get('/user-resume-position', { certCode: selectedCertification });
        if (apiResume?.question_id && normalized.some(q => q.id === apiResume.question_id)) {
          setResumeData({ questionId: String(apiResume.question_id), filterState: apiResume.filter_state ?? {} });
        }
      } catch {}

      setLoading(false);
    };
    fetchData();
  }, [selectedCertification, user]);

  // 現在位置・フィルター状態を保存
  useEffect(() => {
    if (!showFilter && questions.length > 0 && questions[currentIndex] && certificationId) {
      const questionId = questions[currentIndex].id;
      const filterState = { selectedTopic, selectedCategory, selectedTask, selectedResult, questionOrder, selectedQuestions };
      localStorage.setItem(`lastQuestion_${selectedCertification}`, String(questionId));
      localStorage.setItem(`lastFilter_${selectedCertification}`, JSON.stringify(filterState));
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        api.put('/user-resume-position', { certificationId, questionId, filterState }).catch(() => {});
      }, 2000);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, showFilter, questions, certificationId, selectedCertification]);

  const buildFilteredList = (overrides = {}) => {
    const topic    = overrides.selectedTopic    ?? selectedTopic;
    const category = overrides.selectedCategory ?? selectedCategory;
    const task     = overrides.selectedTask     ?? selectedTask;
    const qs       = overrides.selectedQuestions ?? selectedQuestions;
    const result   = overrides.selectedResult   ?? selectedResult;

    let filtered = allQuestions;
    if (topic.length > 0)    filtered = filtered.filter(q => topic.includes(q.topic?.toString()));
    if (category.length > 0) filtered = filtered.filter(q => parseSelectableValues(q?.category).some(c => category.includes(c)));
    if (task.length > 0)     filtered = filtered.filter(q => parseSelectableValues(q?.task).some(t => task.includes(t)));
    if (qs.length > 0)       filtered = filtered.filter(q => qs.includes(q.number?.toString()));
    if (result.length > 0) {
      const codes = result.map(l => statusCodeMap[l]).filter(Boolean);
      const inclUnanswered = result.includes('未回答');
      filtered = filtered.filter(q => {
        const r = results[q.id];
        if (!r) return inclUnanswered;
        return codes.includes(r);
      });
    }
    filtered.sort((a, b) => {
      const tc = a.topic?.toString().localeCompare(b.topic?.toString(), 'ja', { numeric: true });
      if (tc !== 0) return tc;
      return a.number?.toString().localeCompare(b.number?.toString(), 'ja', { numeric: true });
    });
    return filtered;
  };

  const handleStart = (startPosition = 1) => {
    const filtered = buildFilteredList();
    setQuestions(filtered);
    if (questionOrder === 'last' && filtered.length > 0) {
      setCurrentIndex(filtered.length - 1);
    } else {
      setCurrentIndex(Math.max(0, Math.min((startPosition || 1) - 1, filtered.length - 1)));
    }
    setShowAnswer(false);
    setShowFilter(false);
  };

  const handleResume = () => {
    if (!resumeData) return;
    const { questionId, filterState } = resumeData;
    setSelectedTopic(filterState.selectedTopic ?? []);
    setSelectedCategory(filterState.selectedCategory ?? []);
    setSelectedTask(filterState.selectedTask ?? []);
    setSelectedResult(filterState.selectedResult ?? []);
    setQuestionOrder(filterState.questionOrder ?? 'first');
    setSelectedQuestions(filterState.selectedQuestions ?? []);
    const filtered = buildFilteredList(filterState);
    setQuestions(filtered);
    const idx = filtered.findIndex(q => String(q.id) === questionId);
    setCurrentIndex(idx >= 0 ? idx : 0);
    setShowAnswer(false);
    setShowFilter(false);
  };

  const updateStatus = async (id, status) => {
    if (!user) return;
    try { await api.put('/user-question-statuses', { question_id: id, status }); }
    catch { return; }
    setResults(prev => {
      const updated = { ...prev, [id]: status };
      setProgressData(calculateProgressData(allQuestions, updated, categoryOptions, taskOptions));
      return updated;
    });
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) { setCurrentIndex(currentIndex + 1); setShowAnswer(false); }
  };

  const handlePrev = (backOne = true) => {
    if (backOne && currentIndex > 0) { setCurrentIndex(currentIndex - 1); setShowAnswer(false); }
    else { setShowFilter(true); setShowAnswer(false); setCurrentIndex(0); }
  };

  const handleSetCurrentQuestion = (updated) => {
    setQuestions(prev => prev.map(q => q.id === updated.id ? updated : q));
    setAllQuestions(prev => prev.map(q => q.id === updated.id ? updated : q));
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: CH.dim, fontFamily: CH.mono, fontSize: 13 }}>
      読み込み中...
    </div>
  );

  return (
    <>
      <ZoomModal zoomImageUrl={zoomImageUrl} onClose={() => setZoomImageUrl(null)} />

      {showFilter ? (
        <MobileFilterScreen
          selectedCertification={selectedCertification}
          certFormalName={certFormalName}
          certVendor={certVendor}
          certUserStatus={certUserStatus}
          examGuideUrl={EXAM_GUIDE_URLS[selectedCertification] ?? null}
          questions={allQuestions}
          results={results}
          progressData={progressData ? { total_correct: progressData.clear, total_incorrect: progressData.error } : null}
          selectedTopic={selectedTopic}       setSelectedTopic={setSelectedTopic}
          categories={categoryOptions}
          selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory}
          tasks={taskOptions}
          selectedTask={selectedTask}         setSelectedTask={setSelectedTask}
          selectedResult={selectedResult}     setSelectedResult={setSelectedResult}
          questionOrder={questionOrder}       setQuestionOrder={setQuestionOrder}
          selectedQuestions={selectedQuestions} setSelectedQuestions={setSelectedQuestions}
          lastQuestionId={resumeData?.questionId}
          resumeFilterState={resumeData?.filterState}
          onResume={handleResume}
          onStart={handleStart}
          onBack={() => window.history.back()}
        />
      ) : questions.length === 0 ? (
        <div style={{ padding: '40px 20px', textAlign: 'center', fontFamily: CH.serif }}>
          <div style={{ color: CH.muted, marginBottom: 16 }}>問題が見つかりませんでした。</div>
          <div
            onClick={() => setShowFilter(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              height: 38, padding: '0 20px',
              background: 'var(--app-color-surface)', border: '1px solid var(--app-color-border)',
              borderRadius: 8, cursor: 'pointer', color: CH.ink, fontSize: 14,
            }}
          >フィルターに戻る</div>
        </div>
      ) : (
        <MobileQuestionView
          currentQuestion={questions[currentIndex]}
          currentIndex={currentIndex}
          totalQuestions={questions.length}
          results={results}
          onSetResult={updateStatus}
          onNext={handleNext}
          onPrev={handlePrev}
          onImageClick={(url) => setZoomImageUrl(url)}
          showAnswer={showAnswer}
          toggleShowAnswer={() => setShowAnswer(!showAnswer)}
          onSetCurrentQuestion={handleSetCurrentQuestion}
          selectedCertification={selectedCertification}
          questionOrder={questionOrder}
          onBackToFilter={() => setShowFilter(true)}
          statusLabelMap={statusLabelMap}
          user={user}
        />
      )}
    </>
  );
}

export default MobileExamView;
